/**
 * The complete published API surface.
 *
 * This file is the single source for the reference pages, the request/response
 * schemas and the four-language snippets. Sandbox tables (test cards, test
 * VPAs, webhook events) are also served live from `GET /api/v1/docs/*` so the
 * documented values can never drift from what the simulator does.
 */

export type Language = 'curl' | 'node' | 'python' | 'php';

export const LANGUAGES: { id: Language; label: string }[] = [
  { id: 'curl', label: 'cURL' },
  { id: 'node', label: 'Node.js' },
  { id: 'python', label: 'Python' },
  { id: 'php', label: 'PHP' },
];

export interface SchemaField {
  name: string;
  type: string;
  required?: boolean;
  description: string;
}

export interface Endpoint {
  id: string;
  method: 'GET' | 'POST' | 'DELETE';
  path: string;
  title: string;
  summary: string;
  idempotent: boolean;
  scope: string;
  request: SchemaField[];
  response: string;
  snippets: Record<Language, string>;
}

/*
 * The full base, including /api and /public.
 *
 * This said `https://api.riskpay.in/v1` and every snippet on the docs site
 * inherited it, so the documented URL 404'd with `unknown_endpoint`. There are
 * four route groups on this API — /public is the one a secret key reaches —
 * and both segments are load-bearing.
 */
const BASE = 'https://api.riskpay.in/api/v1/public';

/* ========================================================================== */
/*  Endpoints                                                                  */
/* ========================================================================== */

export const ENDPOINTS: Endpoint[] = [
  {
    id: 'create-payment',
    method: 'POST',
    path: '/api/v1/public/payments/create',
    title: 'Create a payment',
    summary:
      'Creates a payment in INITIATED state and returns the next action for the chosen instrument. For UPI methods the response carries a ready-to-use intent link and QR payload.',
    idempotent: true,
    scope: 'payments:write',
    request: [
      { name: 'amount', type: 'number | string', required: true, description: 'Rupees, up to two decimal places: 1499.50, "1499.50" or 1499. Send it as a string if your language turns 1499.50 into 1499.4999999999998.' },
      { name: 'currency', type: 'string', required: true, description: 'ISO 4217 code. Must match the currency your account settles in. Defaults to INR.' },
      { name: 'payment_method', type: 'enum', required: true, description: 'UPI, UPI_INTENT, UPI_COLLECT, CREDIT_CARD, DEBIT_CARD, NET_BANKING, WALLET or EMI.' },
      { name: 'merchant_order_id', type: 'string', description: 'Your own order reference. Unique per merchant — a repeat value returns 409 duplicate_order_id.' },
      { name: 'customer', type: 'object', description: '{ reference, email, phone, vpa }. The vpa is required for UPI_COLLECT.' },
      { name: 'card', type: 'object', description: '{ number, expiry_month, expiry_year, cvv, holder_name }. Validated then discarded — only the network and last four digits are stored.' },
      { name: 'description', type: 'string', description: 'Shown to the payer in their UPI app, truncated to 50 characters.' },
      { name: 'callback_url', type: 'string', description: 'Where the payer returns after a hosted checkout.' },
      { name: 'metadata', type: 'object', description: 'Arbitrary key–value pairs echoed back on every payment object and webhook.' },
    ],
    response: `{
  "id": "pay_R7KQ2M4X9B1T",
  "object": "payment",
  "status": "initiated",
  "mode": "test",
  "amount": "1499.50",
  "amount_formatted": "₹1,499.50",
  "currency": "INR",
  "fee": { "amount": "35.39", "currency": "INR", "formatted": "₹35.39" },
  "net_amount": { "amount": "1464.11", "currency": "INR", "formatted": "₹1,464.11" },
  "refunded_amount": { "amount": "0.00", "currency": "INR", "formatted": "₹0.00" },
  "payment_method": "upi_intent",
  "merchant_order_id": "order_4821",
  "risk_flag": "NONE",
  "settled": false,
  "metadata": { "cart_id": "c_9912" },
  "created_at": "2026-08-28T09:14:22.481Z",
  "completed_at": null,
  "next_action": {
    "type": "upi_intent",
    "intentUri": "upi://pay?pa=riskpay%40hdfcbank&pn=Northwind%20Retail&tr=pay_R7KQ2M4X9B1T&am=1499.50&cu=INR&tn=Order%20%234821",
    "appLinks": { "gpay": "tez://upi/pay?...", "phonepe": "phonepe://pay?...", "paytm": "paytmmp://pay?...", "bhim": "bhim://pay?..." },
    "payeeVpa": "riskpay@hdfcbank",
    "amountDisplay": "₹1499.50",
    "expiresAt": "2026-08-28T09:29:22.481Z"
  }
}`,
    snippets: {
      curl: `curl -X POST ${BASE}/payments/create \\
  -H "Authorization: Bearer sk_test_YOUR_SECRET_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: 7f1c2b6e-3a44-4d51-9a02-1b8f0c2d5e77" \\
  -d '{
    "amount": 1499.50,
    "currency": "INR",
    "payment_method": "UPI_INTENT",
    "merchant_order_id": "order_4821",
    "customer": { "email": "asha@example.com", "phone": "+919812345678" },
    "description": "Order #4821",
    "metadata": { "cart_id": "c_9912" }
  }'`,
      node: `import { randomUUID } from 'node:crypto';

const response = await fetch('${BASE}/payments/create', {
  method: 'POST',
  headers: {
    Authorization: 'Bearer ' + process.env.RISKPAY_SECRET_KEY,
    'Content-Type': 'application/json',
    // Reuse the SAME key when you retry, so a network timeout can never
    // create a second payment.
    'Idempotency-Key': randomUUID(),
  },
  body: JSON.stringify({
    amount: 1499.50,               // rupees
    currency: 'INR',
    payment_method: 'UPI_INTENT',
    merchant_order_id: 'order_4821',
    customer: { email: 'asha@example.com', phone: '+919812345678' },
    description: 'Order #4821',
    metadata: { cart_id: 'c_9912' },
  }),
});

if (!response.ok) {
  const { error } = await response.json();
  throw new Error(error.code + ': ' + error.message);
}

const payment = await response.json();
console.log(payment.next_action.intentUri);`,
      python: `import os
import uuid
import requests

response = requests.post(
    "${BASE}/payments/create",
    headers={
        "Authorization": f"Bearer {os.environ['RISKPAY_SECRET_KEY']}",
        "Content-Type": "application/json",
        "Idempotency-Key": str(uuid.uuid4()),
    },
    json={
        "amount": 1499.50,              # rupees
        "currency": "INR",
        "payment_method": "UPI_INTENT",
        "merchant_order_id": "order_4821",
        "customer": {"email": "asha@example.com", "phone": "+919812345678"},
        "description": "Order #4821",
        "metadata": {"cart_id": "c_9912"},
    },
    timeout=15,
)

if not response.ok:
    error = response.json()["error"]
    raise RuntimeError(f"{error['code']}: {error['message']}")

payment = response.json()
print(payment["next_action"]["intentUri"])`,
      php: `<?php
$payload = [
    'amount' => 1499.50,               // rupees
    'currency' => 'INR',
    'payment_method' => 'UPI_INTENT',
    'merchant_order_id' => 'order_4821',
    'customer' => ['email' => 'asha@example.com', 'phone' => '+919812345678'],
    'description' => 'Order #4821',
    'metadata' => ['cart_id' => 'c_9912'],
];

$ch = curl_init('${BASE}/payments/create');
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_POST => true,
    CURLOPT_POSTFIELDS => json_encode($payload),
    CURLOPT_HTTPHEADER => [
        'Authorization: Bearer ' . getenv('RISKPAY_SECRET_KEY'),
        'Content-Type: application/json',
        'Idempotency-Key: ' . bin2hex(random_bytes(16)),
    ],
]);

$body = curl_exec($ch);
$status = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

$payment = json_decode($body, true);
if ($status >= 400) {
    throw new RuntimeException($payment['error']['code'] . ': ' . $payment['error']['message']);
}

echo $payment['next_action']['intentUri'];`,
    },
  },

  {
    id: 'retrieve-payment',
    method: 'GET',
    path: '/api/v1/public/payments/:id',
    title: 'Retrieve a payment',
    summary:
      'Fetches the current state of a payment. Poll this as a fallback, but treat webhooks as the primary signal — a payer can complete a UPI collect long after your checkout page has closed.',
    idempotent: false,
    scope: 'payments:read',
    request: [{ name: 'id', type: 'string', required: true, description: 'The gateway payment id, e.g. pay_R7KQ2M4X9B1T. Path parameter.' }],
    response: `{
  "id": "pay_R7KQ2M4X9B1T",
  "object": "payment",
  "status": "success",
  "amount": "1499.50",
  "amount_formatted": "₹1,499.50",
  "fee": { "amount": "35.39", "currency": "INR", "formatted": "₹35.39" },
  "net_amount": { "amount": "1464.11", "currency": "INR", "formatted": "₹1,464.11" },
  "payment_method": "upi_intent",
  "upi": { "vpa": "asha@okhdfcbank", "rrn": "418377210094" },
  "acquirer_reference": "sbx_m3k9x1z",
  "failure": null,
  "settled": false,
  "completed_at": "2026-08-28T09:16:04.902Z"
}`,
    snippets: {
      curl: `curl ${BASE}/payments/pay_R7KQ2M4X9B1T \\
  -H "Authorization: Bearer sk_test_YOUR_SECRET_KEY"`,
      node: `const response = await fetch('${BASE}/payments/pay_R7KQ2M4X9B1T', {
  headers: { Authorization: 'Bearer ' + process.env.RISKPAY_SECRET_KEY },
});

const payment = await response.json();
if (payment.status === 'success') {
  await fulfilOrder(payment.merchant_order_id);
}`,
      python: `import os, requests

payment = requests.get(
    "${BASE}/payments/pay_R7KQ2M4X9B1T",
    headers={"Authorization": f"Bearer {os.environ['RISKPAY_SECRET_KEY']}"},
    timeout=15,
).json()

if payment["status"] == "success":
    fulfil_order(payment["merchant_order_id"])`,
      php: `<?php
$ch = curl_init('${BASE}/payments/pay_R7KQ2M4X9B1T');
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => ['Authorization: Bearer ' . getenv('RISKPAY_SECRET_KEY')],
]);
$payment = json_decode(curl_exec($ch), true);
curl_close($ch);

if ($payment['status'] === 'success') {
    fulfil_order($payment['merchant_order_id']);
}`,
    },
  },

  {
    id: 'create-upi-intent',
    method: 'POST',
    path: '/api/v1/public/upi/intent',
    title: 'Generate a UPI Intent',
    summary:
      'Routes the payment to an upstream provider and returns its NPCI-compliant upi:// deep link, a link for every major UPI app, and a QR image URL. Pass an existing payment_id to attach the intent to a payment you already created, or an amount to create one in the same call.',
    idempotent: true,
    scope: 'payments:write',
    request: [
      { name: 'payment_id', type: 'string', description: 'Existing payment to attach the intent to. Must still be INITIATED or PENDING.' },
      { name: 'amount', type: 'number | string', description: 'Rupees. Required when payment_id is omitted.' },
      { name: 'merchant_order_id', type: 'string', description: 'Your order reference, when creating a new payment.' },
      { name: 'note', type: 'string', description: 'Transaction note shown in the payer app. Truncated to 50 characters by the UPI spec.' },
      { name: 'vpa', type: 'string', description: 'Preferred payee VPA. Ignored when the selected route supplies its own, which most do. Must match handle@psp.' },
      { name: 'customer', type: 'object', description: '{ name, email, phone }. Passed to the upstream route and stored on the payment.' },
    ],
    response: `{
  "payment_id": "pay_R7KQ2M4X9B1T",
  "object": "upi_intent",
  "amount": "1499.50",
  "amount_display": "₹1,499.50",
  "currency": "INR",
  "intent_uri": "upi://pay?pa=riskpay%40hdfcbank&pn=Northwind%20Retail&tr=pay_R7KQ2M4X9B1T&am=1499.50&cu=INR&tn=Order%20%234821",
  "app_links": {
    "gpay":       "tez://upi/pay?pa=riskpay%40hdfcbank&...",
    "phonepe":    "phonepe://pay?pa=riskpay%40hdfcbank&...",
    "paytm":      "paytmmp://pay?pa=riskpay%40hdfcbank&...",
    "bhim":       "bhim://pay?pa=riskpay%40hdfcbank&...",
    "amazonpay":  "amazonpay://pay?...",
    "cred":       "credpay://upi/pay?...",
    "mobikwik":   "mobikwik://upi/pay?...",
    "freecharge": "freecharge://upi/pay?..."
  },
  "apps": [
    { "key": "gpay", "name": "Google Pay", "link": "tez://upi/pay?...",
      "android_package": "com.google.android.apps.nbu.paisa.user" }
  ],
  "qr": {
    "url":     "https://api.riskpay.in/api/v1/qr/pay_R7KQ2M4X9B1T.png",
    "svg_url": "https://api.riskpay.in/api/v1/qr/pay_R7KQ2M4X9B1T.svg",
    "payload": "upi://pay?pa=riskpay%40hdfcbank&..."
  },
  "payee_vpa": "riskpay@hdfcbank",
  "expires_at": "2026-08-28T09:29:22.481Z",
  "routed": true
}`,
    snippets: {
      curl: `curl -X POST ${BASE}/upi/intent \\
  -H "Authorization: Bearer sk_test_YOUR_SECRET_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: 9c0a51de-6b2f-4e83-b7d1-0f5a2c9e4411" \\
  -d '{ "amount": 1499.50, "merchant_order_id": "order_4821", "note": "Order #4821" }'`,
      node: `const intent = await riskpay('/upi/intent', {
  amount: 1499.50,
  merchant_order_id: 'order_4821',
  note: 'Order #4821',
});

// On mobile web, hand the payer the generic link and let the OS pick the app.
window.location.href = intent.intent_uri;

// On desktop, render <img src={intent.qr.url}> instead — we generate it.`,
      python: `intent = riskpay_post(
    "/upi/intent",
    {"amount": 1499.50, "merchant_order_id": "order_4821", "note": "Order #4821"},
)

# Show intent["qr"]["url"] as an <img> on desktop,
# or redirect to intent["intent_uri"] on mobile.
print(intent["intent_uri"])`,
      php: `<?php
$intent = riskpay_post('/upi/intent', [
    'amount' => 1499.50,
    'merchant_order_id' => 'order_4821',
    'note' => 'Order #4821',
]);

header('Location: ' . $intent['intent_uri']);`,
    },
  },

  {
    id: 'create-checkout-session',
    method: 'POST',
    path: '/api/v1/public/checkout/sessions',
    title: 'Create a checkout session',
    summary:
      'Returns a hosted payment page URL and nothing else. Redirect the customer there; we handle the payment instrument, the provider, retries across providers, and the return trip. The response deliberately carries no intent link, VPA or QR — those belong to the page, and a checkout rendered half by you and half by us is a checkout whose result never reaches your database.',
    idempotent: true,
    scope: 'payments:write',
    request: [
      { name: 'amount', type: 'number | string', required: true, description: 'Rupees, up to two decimal places: 1299.50, "1299.50" or 1299.' },
      { name: 'return_url', type: 'string', required: true, description: 'Where the customer is sent when the page finishes, whatever the outcome. We append the result and a signature to it. Must be https in live mode.' },
      { name: 'currency', type: 'string', description: 'ISO 4217. Must match your settlement currency. Defaults to INR.' },
      { name: 'merchant_order_id', type: 'string', description: 'Your own order reference. Echoed back on the return redirect and every webhook.' },
      { name: 'description', type: 'string', description: 'One line shown on the page under the amount. Keep it something the customer will recognise on a card statement.' },
      { name: 'customer', type: 'object', description: '{ name, email, phone }. Prefills the page and is passed to the provider.' },
      { name: 'cancel_url', type: 'string', description: 'Used instead of return_url when the customer abandons deliberately. Defaults to return_url.' },
      { name: 'metadata', type: 'object', description: 'Arbitrary key–value pairs, returned on the session object.' },
    ],
    response: `{
  "id": "cs_8H2KQ4M1XB7T",
  "object": "checkout_session",
  "status": "created",
  "mode": "test",
  "amount": "1299.00",
  "amount_formatted": "₹1,299.00",
  "currency": "INR",
  "merchant_order_id": "order_4821",
  "checkout_url": "https://checkout.riskpay.in/c/9f2c…",
  "return_url": "https://yourshop.example/orders/4821/return",
  "expires_in": 1200,
  "expires_at": "2026-08-28T09:49:22.481Z"
}`,
    snippets: {
      curl: `curl -X POST ${BASE}/checkout/sessions \\
  -H "Authorization: Bearer sk_test_YOUR_SECRET_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: 3f7c19aa-1c2b-4f1e-9a44-9b0d4e2a77c1" \\
  -d '{
    "amount": 1299.00,
    "merchant_order_id": "order_4821",
    "description": "Pro plan, 12 months",
    "return_url": "https://yourshop.example/orders/4821/return"
  }'`,
      node: `const session = await riskpay('/checkout/sessions', {
  amount: 1299.00,
  merchant_order_id: 'order_4821',
  description: 'Pro plan, 12 months',
  return_url: 'https://yourshop.example/orders/4821/return',
});

// Store session.id against the order, then send the customer on their way.
res.redirect(303, session.checkout_url);`,
      python: `session = riskpay_post(
    "/checkout/sessions",
    {
        "amount": 1299.00,
        "merchant_order_id": "order_4821",
        "description": "Pro plan, 12 months",
        "return_url": "https://yourshop.example/orders/4821/return",
    },
)

# Save session["id"] on the order before redirecting.
return redirect(session["checkout_url"], code=303)`,
      php: `<?php
$session = riskpay_post('/checkout/sessions', [
    'amount'            => 1299.00,
    'merchant_order_id' => 'order_4821',
    'description'       => 'Pro plan, 12 months',
    'return_url'        => 'https://yourshop.example/orders/4821/return',
]);

header('Location: ' . $session['checkout_url'], true, 303);`,
    },
  },

  {
    id: 'retrieve-checkout-session',
    method: 'GET',
    path: '/api/v1/public/checkout/sessions/{id}',
    title: 'Retrieve a checkout session',
    summary:
      'Reads the current state of a session, including the payment id once one exists. Use it to reconcile an order whose webhook you may have missed — never as the primary signal, because a customer who closes the tab never triggers a read.',
    idempotent: false,
    scope: 'payments:read',
    request: [
      { name: 'id', type: 'string', required: true, description: 'The cs_… identifier returned when the session was created. Path parameter.' },
    ],
    response: `{
  "id": "cs_8H2KQ4M1XB7T",
  "object": "checkout_session",
  "status": "paid",
  "mode": "live",
  "amount": "1299.00",
  "amount_formatted": "₹1,299.00",
  "currency": "INR",
  "merchant_order_id": "order_4821",
  "payment_id": "pay_R7KQ2M4X9B1T",
  "checkout_url": "https://checkout.riskpay.in/c/9f2c…",
  "return_url": "https://yourshop.example/orders/4821/return",
  "failure_code": null,
  "expires_at": "2026-08-28T09:49:22.481Z",
  "created_at": "2026-08-28T09:29:22.481Z"
}`,
    snippets: {
      curl: `curl ${BASE}/checkout/sessions/cs_8H2KQ4M1XB7T \\
  -H "Authorization: Bearer sk_test_YOUR_SECRET_KEY"`,
      node: `const session = await riskpay.get('/checkout/sessions/cs_8H2KQ4M1XB7T');
if (session.status === 'paid') await fulfil(session.merchant_order_id);`,
      python: `session = riskpay_get("/checkout/sessions/cs_8H2KQ4M1XB7T")
if session["status"] == "paid":
    fulfil(session["merchant_order_id"])`,
      php: `<?php
$session = riskpay_get('/checkout/sessions/cs_8H2KQ4M1XB7T');
if ($session['status'] === 'paid') {
    fulfil($session['merchant_order_id']);
}`,
    },
  },

  {
    id: 'create-refund',
    method: 'POST',
    path: '/api/v1/public/refunds',
    title: 'Create a refund',
    summary:
      'Refunds a captured payment in full or in part. Omit amount for a full refund of whatever is still refundable. If the payment has not settled the funds come back out of your pending balance; if it has, they come from your available balance.',
    idempotent: true,
    scope: 'refunds:write',
    request: [
      { name: 'payment_id', type: 'string', required: true, description: 'The payment to refund, e.g. pay_R7KQ2M4X9B1T.' },
      { name: 'amount', type: 'integer | string', description: 'Rupees. Omit for a full refund. Cannot exceed the remaining refundable balance.' },
      { name: 'reason', type: 'string', description: 'Free text, surfaced to your support team and in the audit log.' },
      { name: 'speed', type: 'enum', description: 'NORMAL (default) settles on the acquirer cycle. INSTANT debits your balance immediately.' },
      { name: 'metadata', type: 'object', description: 'Arbitrary key–value pairs.' },
    ],
    response: `{
  "id": "rfnd_4KQ9M2X7B1TR",
  "object": "refund",
  "payment_id": "pay_R7KQ2M4X9B1T",
  "amount": { "amount": "499.50", "currency": "INR", "formatted": "₹499.50" },
  "status": "processed",
  "reason": "Customer returned one item",
  "speed": "normal",
  "arn": "ARN000418377210",
  "created_at": "2026-08-28T11:02:41.117Z",
  "processed_at": "2026-08-28T11:02:41.402Z"
}`,
    snippets: {
      curl: `curl -X POST ${BASE}/refunds \\
  -H "Authorization: Bearer sk_test_YOUR_SECRET_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: 2b8f1a09-77c4-4a1e-9f30-6d5b2c1e8804" \\
  -d '{
    "payment_id": "pay_R7KQ2M4X9B1T",
    "amount": 499.50,
    "reason": "Customer returned one item"
  }'`,
      node: `const refund = await riskpay('/refunds', {
  payment_id: 'pay_R7KQ2M4X9B1T',
  amount: 499.50,                    // partial refund, in rupees
  reason: 'Customer returned one item',
});

console.log(refund.status, refund.arn);`,
      python: `refund = riskpay_post(
    "/refunds",
    {
        "payment_id": "pay_R7KQ2M4X9B1T",
        "amount": 499.50,             # partial refund, in rupees
        "reason": "Customer returned one item",
    },
)

print(refund["status"], refund["arn"])`,
      php: `<?php
$refund = riskpay_post('/refunds', [
    'payment_id' => 'pay_R7KQ2M4X9B1T',
    'amount' => 499.50,               // partial refund, in rupees
    'reason' => 'Customer returned one item',
]);

echo $refund['status'] . ' ' . $refund['arn'];`,
    },
  },

  {
    id: 'create-payout',
    method: 'POST',
    path: '/api/v1/public/payouts',
    title: 'Create a payout',
    summary:
      'Sends funds from your available balance to a beneficiary over IMPS, NEFT, RTGS or UPI. The balance check and the debit happen under one row lock, so a duplicate request can never overdraw the account.',
    idempotent: true,
    scope: 'payouts:write',
    request: [
      { name: 'amount', type: 'integer | string', required: true, description: 'Rupees. Fees are added on top and debited from the same balance.' },
      { name: 'currency', type: 'string', description: 'Defaults to INR.' },
      { name: 'purpose', type: 'enum', description: 'VENDOR_PAYMENT, BULK_PAYOUT, REFUND_FUNDING, SALARY, SETTLEMENT or OTHER.' },
      { name: 'rail', type: 'enum', description: 'IMPS (default), NEFT, RTGS or UPI.' },
      { name: 'beneficiary', type: 'object', required: true, description: '{ name, account_number, ifsc } or { name, vpa }. Account numbers are encrypted at rest with AES-256-GCM.' },
      { name: 'narration', type: 'string', description: 'Appears on the beneficiary bank statement where the rail supports it.' },
    ],
    response: `{
  "id": "pout_9M2X7B1TR4KQ",
  "object": "payout",
  "amount": { "amount": "25000.00", "currency": "INR", "formatted": "₹25,000.00" },
  "fee": { "amount": "5.90", "currency": "INR", "formatted": "₹5.90" },
  "total_debited": { "amount": "25005.90", "currency": "INR", "formatted": "₹25,005.90" },
  "purpose": "vendor_payment",
  "rail": "IMPS",
  "status": "queued",
  "utr": null,
  "beneficiary": { "name": "Kesari Logistics", "account_last4": "4471", "ifsc": "HDFC0000123", "vpa": null },
  "created_at": "2026-08-28T12:31:09.554Z"
}`,
    snippets: {
      curl: `curl -X POST ${BASE}/payouts \\
  -H "Authorization: Bearer sk_live_YOUR_SECRET_KEY" \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: e41d7a90-2f6b-4c18-88ad-5b3e0c9f1206" \\
  -d '{
    "amount": 25000.00,
    "purpose": "VENDOR_PAYMENT",
    "rail": "IMPS",
    "beneficiary": {
      "name": "Kesari Logistics",
      "account_number": "50100234564471",
      "ifsc": "HDFC0000123"
    },
    "narration": "INV-2026-0918"
  }'`,
      node: `const payout = await riskpay('/payouts', {
  amount: 25000.00,                  // ₹25,000.00
  purpose: 'VENDOR_PAYMENT',
  rail: 'IMPS',
  beneficiary: {
    name: 'Kesari Logistics',
    account_number: '50100234564471',
    ifsc: 'HDFC0000123',
  },
  narration: 'INV-2026-0918',
});

// status is 'queued'. Wait for the payout.processed webhook to get the UTR.
console.log(payout.id, payout.status);`,
      python: `payout = riskpay_post(
    "/payouts",
    {
        "amount": 25000.00,           # ₹25,000.00
        "purpose": "VENDOR_PAYMENT",
        "rail": "IMPS",
        "beneficiary": {
            "name": "Kesari Logistics",
            "account_number": "50100234564471",
            "ifsc": "HDFC0000123",
        },
        "narration": "INV-2026-0918",
    },
)

# The UTR arrives on the payout.processed webhook, not here.
print(payout["id"], payout["status"])`,
      php: `<?php
$payout = riskpay_post('/payouts', [
    'amount' => 25000.00,             // ₹25,000.00
    'purpose' => 'VENDOR_PAYMENT',
    'rail' => 'IMPS',
    'beneficiary' => [
        'name' => 'Kesari Logistics',
        'account_number' => '50100234564471',
        'ifsc' => 'HDFC0000123',
    ],
    'narration' => 'INV-2026-0918',
]);

echo $payout['id'] . ' ' . $payout['status'];`,
    },
  },
];

/* ========================================================================== */
/*  Sandbox test data                                                          */
/* ========================================================================== */

export interface TestCard {
  number: string;
  network: string;
  outcome: string;
  failureCode?: string;
  description: string;
}

export const TEST_CARDS: TestCard[] = [
  { number: '4242 4242 4242 4242', network: 'Visa', outcome: 'SUCCESS', description: 'Authorises and captures immediately.' },
  { number: '5555 5544 4444 3333', network: 'Mastercard', outcome: 'SUCCESS', description: 'Mastercard success path.' },
  { number: '6521 0000 0000 0008', network: 'RuPay', outcome: 'SUCCESS', description: 'RuPay domestic success path.' },
  { number: '4000 0000 0000 0341', network: 'Visa', outcome: 'REQUIRES_3DS', description: 'Forces a 3-D Secure challenge before authorisation.' },
  { number: '4000 0000 0000 9995', network: 'Visa', outcome: 'FAILED', failureCode: 'INSUFFICIENT_FUNDS', description: 'Issuer declines: insufficient funds.' },
  { number: '4000 0000 0000 0101', network: 'Visa', outcome: 'FAILED', failureCode: 'FRAUD_SUSPECTED', description: 'Issuer declines for suspected fraud, and sets risk_flag on the transaction.' },
  { number: '4000 0000 0000 0069', network: 'Visa', outcome: 'FAILED', failureCode: 'EXPIRED_CARD', description: 'Issuer declines: expired card.' },
  { number: '4000 0000 0000 0127', network: 'Visa', outcome: 'FAILED', failureCode: 'INCORRECT_CVC', description: 'Issuer declines: incorrect CVV.' },
  { number: '4000 0000 0000 0119', network: 'Visa', outcome: 'FAILED', failureCode: 'PROCESSING_ERROR', description: 'Acquirer processing error — safe to retry.' },
];

export interface TestVpa {
  vpa: string;
  outcome: string;
  failureCode?: string;
  description: string;
}

export const TEST_VPAS: TestVpa[] = [
  { vpa: 'success@riskpay', outcome: 'SUCCESS', description: 'Collect request is approved instantly and an RRN is issued.' },
  { vpa: 'failure@riskpay', outcome: 'FAILED', failureCode: 'PAYMENT_DECLINED', description: 'Payer declines the collect request.' },
  { vpa: 'pending@riskpay', outcome: 'PENDING', description: 'Stays pending, then expires after the 15-minute window.' },
  { vpa: 'insufficient@riskpay', outcome: 'FAILED', failureCode: 'INSUFFICIENT_FUNDS', description: 'Payer bank rejects for insufficient balance.' },
  { vpa: 'timeout@riskpay', outcome: 'FAILED', failureCode: 'UPI_TIMEOUT', description: 'No response from the PSP within the timeout.' },
];

/* ========================================================================== */
/*  Sandbox behaviour                                                          */
/* ========================================================================== */

export interface SandboxNote {
  title: string;
  body: string;
}

/**
 * What TEST mode does differently. Written from the implementation, not from
 * intent — each of these is a branch that exists in the API today.
 */
export const SANDBOX_NOTES: SandboxNote[] = [
  {
    title: 'No route configured? TEST still works.',
    body:
      'A TEST payment never fails because of route configuration. If no UPI-intent route is ' +
      'available — none configured, all disabled, all saturated — RiskPay generates the intent ' +
      'itself and returns it exactly as a real route would. Route configuration is an operations ' +
      'concern and should not block an integration.',
  },
  {
    title: 'Checkout falls back to a sandbox page.',
    body:
      'If a TEST checkout session has no CHECKOUT route available, `POST /checkout/{token}/pay` ' +
      'returns `"sandbox": true` and a `payment_url` pointing at a RiskPay-hosted page. That page ' +
      'shows the amount and two buttons — simulate success, or simulate failure.',
  },
  {
    title: 'The sandbox page is not a shortcut.',
    body:
      'Whichever button is pressed runs the real capture path: the same double-entry ledger ' +
      'postings, the same balance movement, the same signed return URL, and the same ' +
      '`payment.success` or `payment.failed` webhook. Only the acquirer’s answer is simulated.',
  },
  {
    title: 'It is gated on the session, not the environment.',
    body:
      'The sandbox page is available on the production API, because that is the host integrators ' +
      'point at. It refuses any session whose mode is LIVE, so a real payment can never be settled ' +
      'by pressing a button on a web page.',
  },
  {
    title: 'LIVE mode has no fallback.',
    body:
      'None of the above applies to a `sk_live_` key. A live payment with no available route ' +
      'returns an error, because silently simulating one would be worse than failing.',
  },
];

/* ========================================================================== */
/*  Webhooks                                                                   */
/* ========================================================================== */

export interface WebhookEventDoc {
  type: string;
  when: string;
  payload: string;
}

export const WEBHOOK_EVENTS: WebhookEventDoc[] = [
  {
    type: 'payment.success',
    when: 'A payment reaches SUCCESS. This is the only event you should fulfil an order on.',
    payload: `{
  "payment_id": "pay_R7KQ2M4X9B1T",
  "amount": "1499.50",
  "currency": "INR",
  "fee": "35.39",
  "net_amount": "1464.11",
  "method": "UPI_INTENT",
  "rrn": "418377210094",
  "merchant_order_id": "order_4821"
}`,
  },
  {
    type: 'payment.failed',
    when: 'A payment reaches FAILED, whether declined by the issuer or abandoned by the payer.',
    payload: `{
  "payment_id": "pay_8VN3PC5QW2ZR",
  "amount": "89900",
  "failure_code": "INSUFFICIENT_FUNDS"
}`,
  },
  {
    type: 'refund.processed',
    when: 'A refund is confirmed by the acquirer and an ARN is available.',
    payload: `{
  "refund_id": "rfnd_4KQ9M2X7B1TR",
  "payment_id": "pay_R7KQ2M4X9B1T",
  "amount": "49950",
  "currency": "INR",
  "status": "processed"
}`,
  },
  {
    type: 'settlement.completed',
    when: 'A settlement batch is paid out to your bank account. Carries the UTR for reconciliation.',
    payload: `{
  "settlement_id": "setl_9F2KQ4M1XB7T",
  "amount": "124976162",
  "currency": "INR",
  "transaction_count": 142,
  "cycle": "T1"
}`,
  },
  {
    type: 'payout.processed',
    when: 'A payout leaves the bank successfully. The UTR is final at this point.',
    payload: `{
  "payout_id": "pout_9M2X7B1TR4KQ",
  "utr": "RP2608260000418377",
  "status": "success"
}`,
  },
  {
    type: 'dispute.created',
    when: 'A chargeback is raised against one of your payments. Evidence is due by evidence_due_at.',
    payload: `{
  "dispute_id": "disp_1TR4KQ9M2X7B",
  "payment_id": "pay_8VN3PC5QW2ZR",
  "amount": "104900",
  "category": "FRAUD",
  "evidence_due_at": "2026-09-11T23:59:59.000Z"
}`,
  },
];

export const VERIFICATION_SNIPPETS: Record<Language, string> = {
  curl: `# Signature verification cannot be done in a shell in any meaningful way —
# the check has to run inside your webhook handler, before you parse the body.
#
# What arrives on the wire:
#
#   POST /webhooks/riskpay HTTP/1.1
#   Content-Type: application/json
#   RiskPay-Signature: t=1756377600,v1=8f3c1e...c0b2
#   RiskPay-Event-Id: 3b1c9c2e-6f4a-4d78-9c11-7a2e5b0d4f83
#   RiskPay-Event-Type: payment.success
#
#   {"id":"3b1c9c2e-...","type":"payment.success","data":{...}}
#
# The signed string is:  <t>.<raw body>
# The digest is:         HMAC-SHA256(endpoint_secret, signed_string)
#
# Three rules, in order of how often they are got wrong:
#   1. Sign the RAW bytes. Re-serialising parsed JSON reorders keys and the
#      MAC will never match.
#   2. Reject a timestamp more than 300 seconds from now — that is what stops
#      a captured request being replayed tomorrow.
#   3. Compare in constant time. A === on hex strings leaks the digest.`,

  node: `import express from 'express';
import crypto from 'node:crypto';

const app = express();
const SECRET = process.env.RISKPAY_WEBHOOK_SECRET;   // whsec_...
const TOLERANCE_SECONDS = 300;

// express.raw() is essential: the signature covers the exact bytes we received.
app.post('/webhooks/riskpay', express.raw({ type: 'application/json' }), (req, res) => {
  const header = req.get('RiskPay-Signature') ?? '';
  const rawBody = req.body.toString('utf8');

  // Header format: t=1756377600,v1=<hex hmac>
  const parts = Object.fromEntries(
    header.split(',').map((chunk) => chunk.trim().split('=')),
  );
  const timestamp = Number(parts.t);
  const provided = parts.v1;

  if (!Number.isFinite(timestamp) || !provided) {
    return res.status(400).send('malformed signature header');
  }

  // 1. Replay window.
  const age = Math.abs(Math.floor(Date.now() / 1000) - timestamp);
  if (age > TOLERANCE_SECONDS) {
    return res.status(400).send('timestamp outside tolerance');
  }

  // 2. Recompute over "<timestamp>.<raw body>".
  const expected = crypto
    .createHmac('sha256', SECRET)
    .update(timestamp + '.' + rawBody)
    .digest();
  const supplied = Buffer.from(provided, 'hex');

  // 3. Constant-time compare — length check first, timingSafeEqual throws
  //    on a length mismatch.
  if (supplied.length !== expected.length || !crypto.timingSafeEqual(supplied, expected)) {
    return res.status(400).send('signature mismatch');
  }

  const event = JSON.parse(rawBody);

  // Deliveries retry, so handle every event idempotently. event.id is stable
  // across all attempts of the same event.
  if (alreadyProcessed(event.id)) return res.status(200).send('ok');

  switch (event.type) {
    case 'payment.success':
      fulfilOrder(event.data.merchant_order_id);
      break;
    case 'refund.processed':
      markRefunded(event.data.refund_id);
      break;
    case 'settlement.completed':
      reconcile(event.data.settlement_id, event.data.amount);
      break;
  }

  // Acknowledge fast. Anything slower than 8 seconds is treated as a failure
  // and retried — do the real work on a queue.
  res.status(200).send('ok');
});`,

  python: `import hmac
import hashlib
import os
import time

from flask import Flask, request, abort

app = Flask(__name__)
SECRET = os.environ["RISKPAY_WEBHOOK_SECRET"].encode()   # whsec_...
TOLERANCE_SECONDS = 300


@app.post("/webhooks/riskpay")
def riskpay_webhook():
    header = request.headers.get("RiskPay-Signature", "")
    raw_body = request.get_data()          # RAW bytes — do not use request.json here

    parts = dict(
        chunk.strip().split("=", 1)
        for chunk in header.split(",")
        if "=" in chunk
    )

    try:
        timestamp = int(parts["t"])
        provided = bytes.fromhex(parts["v1"])
    except (KeyError, ValueError):
        abort(400, "malformed signature header")

    # 1. Replay window.
    if abs(int(time.time()) - timestamp) > TOLERANCE_SECONDS:
        abort(400, "timestamp outside tolerance")

    # 2. Recompute over "<timestamp>.<raw body>".
    signed_payload = str(timestamp).encode() + b"." + raw_body
    expected = hmac.new(SECRET, signed_payload, hashlib.sha256).digest()

    # 3. Constant-time compare.
    if not hmac.compare_digest(provided, expected):
        abort(400, "signature mismatch")

    event = request.get_json()

    # Retries reuse the same event id — dedupe on it.
    if already_processed(event["id"]):
        return "ok", 200

    if event["type"] == "payment.success":
        fulfil_order(event["data"]["merchant_order_id"])
    elif event["type"] == "settlement.completed":
        reconcile(event["data"]["settlement_id"], event["data"]["amount"])

    return "ok", 200`,

  php: `<?php
// RiskPay webhook receiver — signature verification.

$secret = getenv('RISKPAY_WEBHOOK_SECRET');   // whsec_...
$tolerance = 300;

$header = $_SERVER['HTTP_RISKPAY_SIGNATURE'] ?? '';
$rawBody = file_get_contents('php://input');  // RAW bytes, before any decoding

$parts = [];
foreach (explode(',', $header) as $chunk) {
    $pair = explode('=', trim($chunk), 2);
    if (count($pair) === 2) {
        $parts[$pair[0]] = $pair[1];
    }
}

if (!isset($parts['t'], $parts['v1'])) {
    http_response_code(400);
    exit('malformed signature header');
}

// 1. Replay window.
if (abs(time() - (int) $parts['t']) > $tolerance) {
    http_response_code(400);
    exit('timestamp outside tolerance');
}

// 2. Recompute over "<timestamp>.<raw body>".
$expected = hash_hmac('sha256', $parts['t'] . '.' . $rawBody, $secret);

// 3. Constant-time compare.
if (!hash_equals($expected, $parts['v1'])) {
    http_response_code(400);
    exit('signature mismatch');
}

$event = json_decode($rawBody, true);

// Retries reuse the same event id — dedupe on it.
if (already_processed($event['id'])) {
    http_response_code(200);
    exit('ok');
}

switch ($event['type']) {
    case 'payment.success':
        fulfil_order($event['data']['merchant_order_id']);
        break;
    case 'settlement.completed':
        reconcile($event['data']['settlement_id'], $event['data']['amount']);
        break;
}

http_response_code(200);
echo 'ok';`,
};

/* ========================================================================== */
/*  Error catalogue                                                            */
/* ========================================================================== */

export interface ErrorRow {
  status: number;
  type: string;
  code: string;
  message: string;
  action: string;
}

export const ERROR_CODES: ErrorRow[] = [
  { status: 400, type: 'invalid_request_error', code: 'validation_failed', message: 'A field is missing or malformed.', action: 'Read error.param for the offending field and correct the request.' },
  { status: 400, type: 'invalid_request_error', code: 'amount_out_of_range', message: 'Amount is below the minimum or above the maximum for this account.', action: 'Check your configured per-transaction limits in the dashboard.' },
  { status: 400, type: 'idempotency_error', code: 'idempotency_key_required', message: 'This endpoint requires an Idempotency-Key header.', action: 'Send a unique UUID per logical operation and reuse it on retries.' },
  { status: 400, type: 'upi_error', code: 'invalid_vpa', message: 'The VPA is not in a valid handle@psp format.', action: 'Validate the VPA client-side before submitting.' },
  { status: 401, type: 'authentication_error', code: 'invalid_api_key', message: 'The API key is missing, malformed or revoked.', action: 'Send Authorization: Bearer sk_live_… and confirm the key is active.' },
  { status: 401, type: 'authentication_error', code: 'invalid_token', message: 'The session token is missing, expired or malformed.', action: 'Refresh the session and retry.' },
  { status: 401, type: 'authentication_error', code: 'two_factor_required', message: 'A valid TOTP code is required to complete sign-in.', action: 'Prompt for the 6-digit code and resubmit.' },
  { status: 402, type: 'insufficient_funds_error', code: 'insufficient_balance', message: 'Available balance does not cover this payout and its fees.', action: 'Fund the account or wait for the next settlement to release.' },
  { status: 403, type: 'authorization_error', code: 'insufficient_permissions', message: 'The key or role lacks the required scope.', action: 'Use a key with the scope named in the message.' },
  { status: 403, type: 'authorization_error', code: 'test_mode_key_on_live_resource', message: 'A test-mode key cannot access live-mode resources.', action: 'Switch to the matching sk_live_ key.' },
  { status: 403, type: 'compliance_error', code: 'kyc_incomplete', message: 'Live processing requires an approved KYC.', action: 'Complete onboarding at merchant.riskpay.in/onboarding.' },
  { status: 403, type: 'compliance_error', code: 'account_frozen', message: 'The merchant account is frozen.', action: 'Contact compliance@riskpay.in with the request_id.' },
  { status: 404, type: 'invalid_request_error', code: 'resource_not_found', message: 'No such resource, or it belongs to another merchant.', action: 'Check the id and the mode — test and live ids are separate namespaces.' },
  { status: 404, type: 'invalid_request_error', code: 'unknown_endpoint', message: 'No route matches this method and path.', action: 'Check the base URL — every merchant endpoint sits under https://api.riskpay.in/api/v1/public. A bare /v1 prefix is the usual cause.' },
  { status: 409, type: 'invalid_request_error', code: 'duplicate_order_id', message: 'A payment already exists for this merchant_order_id.', action: 'Retrieve the existing payment rather than creating a new one.' },
  { status: 409, type: 'idempotency_error', code: 'request_in_progress', message: 'A request with this Idempotency-Key is still being processed.', action: 'Retry after a short delay without changing the body.' },
  { status: 409, type: 'invalid_request_error', code: 'invalid_state_transition', message: 'The resource is not in a state that allows this operation.', action: 'Retrieve the resource and branch on its current status.' },
  { status: 422, type: 'idempotency_error', code: 'idempotency_key_reuse', message: 'This key was already used with a different request body.', action: 'Generate a new key for a genuinely new operation.' },
  { status: 429, type: 'rate_limit_error', code: 'rate_limit_exceeded', message: 'Too many requests in the current window.', action: 'Back off using the RateLimit-Reset header, then retry.' },
  { status: 500, type: 'api_error', code: 'internal_error', message: 'Something failed on our side.', action: 'Retry with the same Idempotency-Key. Quote request_id if it persists.' },
  { status: 503, type: 'api_error', code: 'concurrent_modification', message: 'The resource is locked by another in-flight write.', action: 'Retry with the same Idempotency-Key after a short backoff.' },
];

/* ========================================================================== */
/*  Navigation                                                                 */
/* ========================================================================== */

export interface NavSection {
  id: string;
  label: string;
  items: { id: string; label: string }[];
}

export const NAV: NavSection[] = [
  {
    id: 'start',
    label: 'Getting started',
    items: [
      { id: 'introduction', label: 'Introduction' },
      { id: 'authentication', label: 'Authentication' },
      { id: 'idempotency', label: 'Idempotency' },
      { id: 'money', label: 'Amounts & currency' },
    ],
  },
  {
    id: 'api',
    label: 'API reference',
    items: ENDPOINTS.map((e) => ({ id: e.id, label: e.title })),
  },
  {
    id: 'testing',
    label: 'Sandbox',
    items: [
      { id: 'sandbox-behaviour', label: 'How test mode differs' },
      { id: 'test-cards', label: 'Test cards' },
      { id: 'test-vpas', label: 'Test VPAs' },
    ],
  },
  {
    id: 'routes',
    label: 'Payment routes',
    items: [
      { id: 'route-overview', label: 'How routing works' },
      { id: 'route-intent-contract', label: 'Intent route contract' },
      { id: 'route-checkout-contract', label: 'Checkout route contract' },
      { id: 'route-callback', label: 'Reporting the outcome' },
      { id: 'return-url', label: 'Verifying a return URL' },
    ],
  },
  {
    id: 'webhooks',
    label: 'Webhooks',
    items: [
      { id: 'webhook-events', label: 'Events' },
      { id: 'webhook-signatures', label: 'Signature verification' },
    ],
  },
  {
    id: 'reference',
    label: 'Reference',
    items: [{ id: 'errors', label: 'Error codes' }],
  },
];
